import React, { useState, useEffect } from 'react';
import { Trash2, Star } from 'lucide-react';
import { imageUrlService } from '@/services/ImageUrlService';
import { useImageUrl } from '@/hooks/useImageUrl';
import { debugLogger } from '@/utils/debugLogger';

interface SimplePhotoThumbnailProps {
  src: string;
  alt: string;
  isMainPhoto?: boolean;
  isDeleting?: boolean;
  onRemove?: () => void;
  onSetMain?: () => void;
  className?: string;
}

export const SimplePhotoThumbnail: React.FC<SimplePhotoThumbnailProps> = ({
  src,
  alt,
  isMainPhoto = false,
  isDeleting = false,
  onRemove,
  onSetMain,
  className = ''
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [currentUrl, setCurrentUrl] = useState<string>('');
  const [fallbackUrls, setFallbackUrls] = useState<string[]>([]);
  const [currentFallbackIndex, setCurrentFallbackIndex] = useState<number>(-1);

  // Get 500x500 card variant URL using async hook (supports v1.5 signed URLs)
  const { url: processedUrl, isLoading: urlLoading } = useImageUrl(src, 'card');

  // Update current URL when processed URL is ready
  useEffect(() => {
    if (!processedUrl) return;

    debugLogger.debug('photo-display', 'SimplePhotoThumbnail URL processed:', {
      original: src,
      processed: processedUrl
    });

    setCurrentUrl(processedUrl);
    
    // Generate fallbacks for backward compatibility with v1 public photos
    // (v1.5 signed URLs don't support fallbacks)
    const fallbacks = imageUrlService.generateFallbackUrls(src, 'card');
    setFallbackUrls(fallbacks);
    setCurrentFallbackIndex(-1);
    setIsLoaded(false);
    setHasError(false);
  }, [processedUrl, src]);

  const handleImageLoad = () => {
    setIsLoaded(true);
    setHasError(false);
    debugLogger.debug('photo-display', 'Image loaded successfully:', currentUrl);
  };

  const handleImageError = () => {
    debugLogger.warn('photo-display', 'Image failed to load:', currentUrl);
    
    // Try next fallback
    const nextIndex = currentFallbackIndex + 1;
    if (nextIndex < fallbackUrls.length) {
      debugLogger.debug('photo-display', 'Trying fallback:', {
        index: nextIndex,
        url: fallbackUrls[nextIndex]
      });
      setCurrentUrl(fallbackUrls[nextIndex]);
      setCurrentFallbackIndex(nextIndex);
      setIsLoaded(false);
    } else {
      debugLogger.error('photo-display', 'All fallbacks exhausted for:', src);
      setHasError(true);
      setIsLoaded(false);
    }
  };

  if (!currentUrl || hasError) {
    return (
      <div className={`aspect-square bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center rounded-lg ${className}`}>
        <div className="text-white/40 text-2xl">📸</div>
      </div>
    );
  }

  return (
    <div className={`relative aspect-square overflow-hidden rounded-lg group ${className}`}>
      {/* Loading state */}
      {!isLoaded && (
        <div className="absolute inset-0 bg-gradient-to-br from-gray-800 to-gray-900 animate-pulse z-20 rounded-lg" />
      )}
      
      {/* Background image with blur and cover */}
      <div 
        className={`absolute inset-0 transition-opacity duration-300 ${
          isLoaded ? 'opacity-100' : 'opacity-0'
        }`}
        style={{
          backgroundImage: `url(${currentUrl})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          filter: 'blur(5px)'
        }}
      />
      
      {/* Main image with contain and higher z-index */}
      <div 
        className={`absolute inset-0 z-10 transition-opacity duration-300 ${
          isLoaded ? 'opacity-100' : 'opacity-0'
        }`}
        style={{
          backgroundImage: `url(${currentUrl})`,
          backgroundSize: 'contain',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      />
      
      {/* Hidden img for loading detection */}
      <img
        src={currentUrl}
        alt={alt}
        className="sr-only"
        onLoad={handleImageLoad}
        onError={handleImageError}
        loading="eager"
      />

      {/* Main photo indicator */}
      {isMainPhoto && (
        <div className="absolute top-2 left-2 z-20">
          <div className="flex items-center gap-1 bg-luxury-gold/90 text-black px-2 py-1 rounded text-xs font-medium">
            <Star className="h-3 w-3 fill-current" />
            Principal
          </div>
        </div>
      )}

      {/* Remove button */}
      {onRemove && (
        <button
          onClick={onRemove}
          className="absolute top-2 right-2 z-20 bg-red-500/90 hover:bg-red-600 text-white p-1.5 rounded-full transition-colors duration-200"
          aria-label="Remover foto"
        >
          <Trash2 className="h-3 w-3" />
        </button>
      )}

      {/* Set as main button */}
      {onSetMain && !isMainPhoto && (
        <button
          onClick={onSetMain}
          className="absolute bottom-2 left-2 z-20 bg-white/90 hover:bg-white text-black px-2 py-1 rounded text-xs transition-colors duration-200"
          aria-label="Definir como principal"
        >
          <Star className="h-3 w-3 inline mr-1" />
          Principal
        </button>
      )}

      {/* Deleting overlay */}
      {isDeleting && (
        <div className="absolute inset-0 bg-black/70 flex items-center justify-center rounded-lg z-30">
          <div className="flex flex-col items-center gap-2">
            <div className="animate-spin rounded-full h-6 w-6 border-2 border-white border-t-transparent"></div>
            <span className="text-white text-xs">Removendo...</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default SimplePhotoThumbnail;